import attachmentsService from '../services/attachmentsService.js';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue.js';
import AnalyticsLogger from '../helpers/analytics/analyticsLogger';
import DAO from './DAO.js';
import Metadata from '../helpers/metadata/Metadata';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import connectivityHeartbeat from '../helpers/ConnectivityHeartbeat';  
import MobileFiles from '../helpers/MobileFiles';

export const attachments_root = {
  attachments: { conversions: {}, additionalFields: ['conversions', 'upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'] },
};

export default class AttachmentsDAO extends DAO {

  constructor(store) {
    super(store, 'attachment');
    this.OfflineQueue = OfflineQueue;
    this.pendingAttachmentRequests = new Map();
    this.handleResponse = this.handleResponse.bind(this);
    this.unsetAttachment = this.unsetAttachment.bind(this);
    this.clearAttachments = this.clearAttachments.bind(this);
    this.markDirty = this.markDirty.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
    this.setAttachmentInAttachable = this.setAttachmentInAttachable.bind(this);
    this.deleteAttachmentFromAttachable = this.deleteAttachmentFromAttachable.bind(this);
    this._creationIndex = 0;
  }

  markDirty(attachment, dirtyState) {
    super.markDirty(attachment, dirtyState);
    if (attachment.attachable_type && attachment.attachable_id) {
      if (attachment.attachable_type == 'CompletionCriterion') {
        const cc = dataModel.completionCriteriaDAO.findItem(attachment.attachable_id); 
        if (cc) {
          dataModel.completionCriteriaDAO.markDirty(cc, dirtyState);
        }
      }
      else if (attachment.attachable_type == 'Delay') {
        const delay = dataModel.delaysDAO.findItem(attachment.attachable_id); 
        if (delay) {
          dataModel.delaysDAO.markDirty(delay, dirtyState);
        }
      }
    }
  }

  get creationIndex() {
    if (!this._creationIndex) {
      this._creationIndex = 0;
    }
    this._creationIndex--;
    return this._creationIndex;
  }

  unsetAttachment(id) {
    StoreItemProcessing.deleteItemInStore(this.itemType, id);
  }

  clearAttachments() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  setAttachmentInAttachable(attachment, attachmentToReplace, dao) {
    const attachableItem = dao.findItem(attachment.attachable_id);
    if (attachableItem) {
      let found = false;
      let needsSave = false;
      attachableItem.attachments = attachableItem.attachments ? attachableItem.attachments.map(attach => {
        if (attach == attachment.id) {
          found = true;
        } else if (attachmentToReplace && attachmentToReplace.id == attach) {
          attach = attachment.id;
          found = true;
          needsSave = true;
        }
        return attach;
      }) : [];
      if (needsSave || !found) {
        if (!found) {
          attachableItem.attachments.push(attachment.id);
        }
        dao.setItemInStore(attachableItem);
      }
    }
    return attachableItem;
  }

  deleteAttachmentFromAttachable(attachment, dao) {
    const attachableItem = dao.findItem(attachment.attachable_id);
    if (attachableItem) {
      const indexToDelete = attachableItem.attachments.findIndex(id => {
        return id == attachment.id;
      });
      if (indexToDelete >= 0) {
        attachableItem.attachments.splice(indexToDelete, 1);
        dao.setItemInStore(attachableItem);
      }
    }
    return attachableItem;
  }

  handleAnalytics(activity, requested_file_type) {
    if (!this.analyticsLogger) {
      const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
      this.analyticsLogger = new AnalyticsLogger({
        currentRole,
        isMobile: !!window.cordova,
      });
    }
    switch (activity) {
    case 'update':
      this.analyticsLogger.logUploadEvent(requested_file_type);
      break;
    case 'create':
      this.analyticsLogger.logUploadEvent(requested_file_type);
      break;
    default:
      break;
    }
  }

  async getAttachment(attachmentId, options) {
    const optionsPassed = options || { recordDescriptor: { attachment: { user: { } } } };
    return this._getItem(attachmentId, attachmentsService.getAttachment.bind(this, attachmentId, optionsPassed), optionsPassed);
  }

  async handleResponse(attachment, response, activity, errorMessage, eventMetadatum, options = {}) {
    if (response.offline) {
      // since we have disabled the heartbeat... and we think we are offline...
      // we will trigger the heartbeat to notify offline (lest we continually
      // try to upload this attachment)
      connectivityHeartbeat.notifyObservers(false, true);
      return this.handleOfflineResponse(attachment, activity, eventMetadatum, options);
    }
    else if (response.error) {
      if (attachment && attachment.id < 0) {
        // if here a new upload that may or may not have uploaded so clean
        // up as best we can...
        if (MobileFiles.available() && attachment.upload.filename) {
          if (await MobileFiles.deleteFile(attachment.upload.filename)) {
            delete attachment.upload.filename;
          }
        }
        this.deleteAttachmentFromAttachable(attachment, dataModel.completionCriteriaDAO);
        StoreItemProcessing.deleteItemInStore(this.itemType, attachment.id);
        const completionCriterion = dataModel.completionCriteriaDAO.findItem(attachment.attachable_id);
        if (completionCriterion) {
          dataModel.completionCriteriaDAO.markNeedsSync(completionCriterion);
          dataModel.completionCriteriaDAO.maybeMarkDirty(completionCriterion);
        }
      }
      dataModel.displayHTTPError(errorMessage, response.error, false);
      this.markError(attachment);
      this.throwItemError(); //make sure to send the denormalized attachment
    }
    else {
      if (activity == 'delete') {
        this.deleteAttachmentFromAttachable(attachment, dataModel.completionCriteriaDAO);
        if (!response.offline) {
          StoreItemProcessing.deleteItemInStore(this.itemType, attachment.id);
        }
        return this.mapToReturn(attachment);
      } 
      else {
        // activity is create
        const attachmentInStore = this.findItem(attachment.id);
        if (MobileFiles.available() && attachment.file_upload) {
          MobileFiles.deleteFile(attachment.file_upload.uri)
            .then(() => {
              console.log('file deleted!');
            })
            .catch((error) => {
              // this is okay as the file may not be in the apps control
              console.log('file delete failed!');
            });
          delete attachment.file_upload;
        }
        else if (MobileFiles.available() && attachment.upload.filename) {
          // if mobile remove the associated file on the phone
          // TODO: perhaps we should wait for an empty offline queue
          // and clean the entire directory? For now just remove the file 
          // directly... NOTE we remove the filename field assuming
          // the delete
          if (await MobileFiles.deleteFile(attachment.upload.filename)) {
            delete attachment.upload.filename;
          }
        }
        attachment = this.normalizeAndSyncToStore(response);
        this.markDirty(attachment, false);
        this.markSynced(attachment);
        if (attachment.attachable_type == 'CompletionCriterion') {
          const completionStep = dataModel.completionCriteriaDAO.findItem(attachment.attachable_id); 
          if (completionStep) {
            this.handleAnalytics(activity, completionStep.requested_file_type);
          }
          this.setAttachmentInAttachable(attachment, attachmentInStore, dataModel.completionCriteriaDAO);
        }
        else if (attachment.attachable_type == 'Delay') {
          const delay = dataModel.delaysDAO.findItem(attachment.attachable_id); 
          if (delay) {
            this.handleAnalytics(activity, delay.requested_file_type);
          }
          this.setAttachmentInAttachable(attachment, attachmentInStore, attachmentInStore, dataModel.delaysDAO);
        }
        if (attachmentInStore) {
          StoreItemProcessing.deleteItemInStore(this.itemType, attachmentInStore.id);
        }
        return this.mapToReturn(this.findItem(attachment.id), options);
      }
    }
  }

  handleOfflineResponse(attachment, activity, eventMetadatum, options) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    this.markDirty(attachment, true); 
    this.OfflineQueue.addQueueItem(this.itemType, activity, { attachment: attachment, eventMetadatum, options });
    return attachment;
  }

  completeOfflineUpdate(queueItem) {
    const attachment = queueItem.itemInfo.attachment;
    if (!attachment) {
      return false;
    }
    switch (queueItem.operationKey) {
    case 'update':
      return this.updateAttachment(attachment, queueItem.itemInfo.eventMetadatum);
    case 'create':
      return this._createAttachment(attachment, queueItem.itemInfo.eventMetadatum).then(response => {
        return response;
      });
    case 'delete':
      return this.deleteAttachment(attachment.id, queueItem.itemInfo.eventMetadatum);
    default:
      break;
    }
    return true;
  }

  /**
  * @param  {object <attachment>} attachment - denormalized attachment to create
  * @param  {object <object>} options.recordDescriptor - used to format returned attachment
  * 
  * @returns {object<Promise<attachment>>} attachment - denormalized attachment
  */
  async createAttachment(attachment, eventMetadatum, options) {

    if (!attachment.created_at) {
      attachment.created_at = new Date().toJSON();
    }
    // ensure store reflects the new attachment
    this.normalizeItem(attachment, { store: true });
    if (attachment.attachable_type == 'CompletionCriterion') {
      // TODO: we need to unset on failure
      this.setAttachmentInAttachable(attachment, null, dataModel.completionCriteriaDAO);
      dataModel.completionCriteriaDAO.setCompleted(attachment.attachable_id);
    }
    else if (attachment.attachable_type == 'Delay') {
      this.setAttachmentInAttachable(attachment, null, dataModel.delaysDAO);
    }
    return Promise.resolve(this.handleOfflineResponse(attachment, 'create', eventMetadatum, options));
  }

  /**
   * 
   * Handles creation of attachments from either blobs or just filenames (a 
   * filename will the case when processing items restored from the redux
   * persistent store)
   *
   * @param {*} attachment 
   * @param {*} eventMetadatum 
   * @param {*} options 
   */
  _createAttachment(attachment, eventMetadatum, options) {
    connectivityHeartbeat.stop();
    try {
      if (attachment.file_upload
          || (attachment.upload && attachment.upload instanceof Blob)) {
        return this._createAttachmentActual(attachment, eventMetadatum, options)
          .then(() => {
            connectivityHeartbeat.start();
          })
          .catch(() => {
            connectivityHeartbeat.start();
          });
      } else if (MobileFiles.available() && attachment.upload.filename) {
        // need to convert file uri to blob first
        attachment.upload.filename = MobileFiles.fixFilename(attachment.upload.filename, attachment.upload.path);
        return MobileFiles.loadBlobFromFilename(attachment.upload.filename).then(blob => {
          // ensure name/filename are set (so upload and retries will work)...
          blob.filename = attachment.upload.filename;
          blob.name = attachment.upload.name;
          attachment.upload = blob;
          return this._createAttachmentActual(attachment, eventMetadatum, options)
            .then(() => {
              connectivityHeartbeat.start();
            })
            .catch(() => {
              connectivityHeartbeat.start();
            });
        }).then(() => {
          connectivityHeartbeat.start();
        }).catch(() => {
          connectivityHeartbeat.start();
        });
      }
    }
    catch(err) {
      // in case of unexpected exception ensure heartbeat restarted...
      connectivityHeartbeat.start();
    }
  }

  /**
   * 
   * Actually attempts to create the attachment and process the results.
   *
   * @param {*} attachment 
   * @param {*} eventMetadatum 
   * @param {*} options 
   */
  _createAttachmentActual(attachment, eventMetadatum, options) {
    return attachmentsService.createAttachment(attachment, eventMetadatum, options, this.updateProgress).then(response => {
      return this.handleResponse(attachment, response, 'create', 'Creating an attachment', eventMetadatum, options);
    });
  }

  /*
  * @param  {object <attachment>} attachment - denormalized attachment to update
  * @param  {object <object>} options.recordDescriptor - used to format returned attachment
  * 
  * @returns {object<Promise<attachment>>} attachment - denormalized attachment
  */
  async updateAttachment(attachment, eventMetadatum, options) {
    return attachmentsService.updateAttachment(attachment, eventMetadatum, options).then(response => {
      return this.handleResponse(attachment, response, 'update', 'Updating an attachment', eventMetadatum, options);
    })
    .catch((error) => {
      //for furture TODOS
      return attachment;
    });
  }

  /**
  * @param  {number} attachmentId - attachment record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  deleteAttachment(attachmentId, eventMetadatum) {
    const attachment = this.findItem(attachmentId);
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    return attachmentsService.deleteAttachment(attachmentId, eventMetadatum).then(response => {
      return this.handleResponse(attachment, response, 'delete', 'Deleting an attachment', eventMetadatum);
    });
  }

  handleActionCableAndGetRequest(acData) {
    const attachment = this.findItem(acData.attachment_id);
    if (attachment) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(attachment);
      }
      else {
        this.markNeedsSync(attachment);
      }
      switch (acData.type) {
      case 'newAttachment':
      case 'updateAttachment':
        return this.getAttachment.bind(this);
      case 'deleteAttachment':
        break;
      default:
      }
    }
  }

  updateProgress(attachment, progress) {
    console.log('update progress callback!');
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_attachments, options.toIgnore.attachments, this.unsetAttachment, this.clearAttachments, options);
  }

}



// WEBPACK FOOTER //
// ./src/dao/AttachmentsDAO.js